import { create } from "zustand";
import { persist } from "zustand/middleware";

import { ChargeDetail } from "@/types/charge";
import { PatientSummary } from "@/types/patient";

import {
  createTenantChangeListener,
  createTenantStorage,
} from "@/stores/tenant-storage-helpers";

// A checkout in progress survives navigation away from the page: the
// checkout writes a snapshot here (tenant-scoped localStorage) as the cart
// changes, the console-wide docked pill reads it from anywhere, and the
// checkout rehydrates from it on return. Snapshots are stored per-patient
// so swapping to a new patient parks the prior cart instead of clobbering
// it, and swapping back picks it up exactly where it was. Cleared on
// payment success and when the cart empties.
export interface CheckoutSnapshot {
  userId: string;
  patient: PatientSummary;
  locationId?: string;
  merchantAccountId?: string;
  charge: ChargeDetail;
  // Set when staff entered checkout via Take Payment on an existing
  // outstanding charge (URL ?chargeId=). The cart in this case mirrors a
  // server-side charge, so the pill resumes via ?chargeId= and we don't
  // hydrate the cart from the snapshot — the server is the source of truth.
  // Absent for the new-cart-in-progress case, which hydrates as before.
  focusedChargeId?: string;
  // Set when this cart is backed by a persisted DRAFT charge. Resuming must
  // preserve that charge id so create_charge promotes the draft in place;
  // replacing only the parent id leaves persisted child items owned by the
  // draft and the API correctly rejects settlement with HTTP 409.
  draftChargeId?: string;
  savedAt: number;
}

// Time-to-live measured from the last write (savedAt is bumped on every
// debounced mirror). A forgotten cart from an hour ago shouldn't silently
// price today's visit.
export const SNAPSHOT_TTL_MS = 60 * 60 * 1000;
export const CHECKOUT_SNAPSHOT_VERSION = 3;

export function isSnapshotFresh(
  snapshot: CheckoutSnapshot | null | undefined,
  now: number = Date.now(),
): snapshot is CheckoutSnapshot {
  return Boolean(snapshot && now - snapshot.savedAt < SNAPSHOT_TTL_MS);
}

// Whether a stored snapshot should hydrate the checkout on mount. Explicit
// URL intent (a deep link targeting a charge or draft charge) wins over the
// snapshot; a different signed-in user never sees another user's cart.
export function shouldHydrateSnapshot(
  snapshot: CheckoutSnapshot | null | undefined,
  context: {
    userId?: string;
    urlPatientId?: string | null;
    urlChargeId?: string | null;
    urlDraftChargeId?: string | null;
    urlCartId?: string | null;
  },
): snapshot is CheckoutSnapshot {
  if (!isSnapshotFresh(snapshot)) return false;
  if (!context.userId || snapshot.userId !== context.userId) return false;
  // Retired cart links must not silently restore an unrelated checkout.
  // This blocks hydration only; it does not load or revive legacy carts.
  if (context.urlChargeId || context.urlDraftChargeId || context.urlCartId) {
    return false;
  }
  if (context.urlPatientId && context.urlPatientId !== snapshot.patient.id) {
    return false;
  }
  return true;
}

// Fresh, resumable snapshots for the signed-in user, sorted most-recent
// first. Used by the console-wide active-checkout pill (one pill per cart)
// and by mount hydration when there's no URL intent (takes the head).
export function pickFreshSnapshots(
  snapshots: Record<string, CheckoutSnapshot>,
  userId: string | undefined,
  now: number = Date.now(),
): CheckoutSnapshot[] {
  if (!userId) return [];
  const out: CheckoutSnapshot[] = [];
  for (const snap of Object.values(snapshots)) {
    if (snap.userId !== userId) continue;
    if (!isSnapshotFresh(snap, now)) continue;
    if ((snap.charge.items?.length ?? 0) === 0) continue;
    out.push(snap);
  }
  out.sort((a, b) => b.savedAt - a.savedAt);
  return out;
}

// Most-recently-updated fresh snapshot belonging to the signed-in user, or
// null if there's nothing worth resuming. Mount hydration uses this when
// there's no URL patient deep link.
export function pickLatestSnapshot(
  snapshots: Record<string, CheckoutSnapshot>,
  userId: string | undefined,
  now: number = Date.now(),
): CheckoutSnapshot | null {
  return pickFreshSnapshots(snapshots, userId, now)[0] ?? null;
}

export function hydratedChargeId(
  snapshot: CheckoutSnapshot,
  defaultChargeId: string,
  hasExistingPayments: boolean,
): string {
  if (snapshot.draftChargeId) return snapshot.draftChargeId;
  return hasExistingPayments ? snapshot.charge.id : defaultChargeId;
}

interface CheckoutSnapshotStore {
  snapshotsByPatientId: Record<string, CheckoutSnapshot>;
  setSnapshot: (snapshot: CheckoutSnapshot) => void;
  clearSnapshot: (patientId: string) => void;
  clearAllSnapshots: () => void;
}

export const useCheckoutSnapshotStore = create<CheckoutSnapshotStore>()(
  persist(
    (set) => ({
      snapshotsByPatientId: {},
      setSnapshot: (snapshot) =>
        set((state) => ({
          snapshotsByPatientId: {
            ...state.snapshotsByPatientId,
            [snapshot.patient.id]: snapshot,
          },
        })),
      clearSnapshot: (patientId) =>
        set((state) => {
          if (!(patientId in state.snapshotsByPatientId)) return state;
          const next = { ...state.snapshotsByPatientId };
          delete next[patientId];
          return { snapshotsByPatientId: next };
        }),
      clearAllSnapshots: () => set({ snapshotsByPatientId: {} }),
    }),
    {
      name: "checkout-v2-snapshot-store",
      partialize: (state) => ({
        snapshotsByPatientId: state.snapshotsByPatientId,
      }),
      storage: createTenantStorage(),
      version: CHECKOUT_SNAPSHOT_VERSION,
      migrate: (persistedState, version) => {
        if (version < CHECKOUT_SNAPSHOT_VERSION) {
          // Older snapshots cannot distinguish a server-backed draft from a
          // local cart. Reusing or rekeying one can submit persisted item IDs
          // under the wrong parent charge, so discard the ambiguous state.
          return { snapshotsByPatientId: {} };
        }
        return persistedState as {
          snapshotsByPatientId: Record<string, CheckoutSnapshot>;
        };
      },
    },
  ),
);

createTenantChangeListener("checkout-v2-snapshot-store", (tenantData) => {
  useCheckoutSnapshotStore.setState({
    snapshotsByPatientId: tenantData.snapshotsByPatientId ?? {},
  });
});

// Single entry point for dropping a parked cart. Every "the parked cart is
// no longer valid" trigger — payment succeeded, checkout was reset, cart
// emptied, staff dismissed the pill, WS payment landed off-page — routes
// through here so there's one place to look, and to update if the store
// shape ever changes. Idempotent — safe to call whether or not a snapshot
// exists for the patient.
export function dismissParkedCart(patientId: string): void {
  useCheckoutSnapshotStore.getState().clearSnapshot(patientId);
}
